import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface VideoModalProps {
  video: any;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Optionally: add left/right arrow navigation for next/prev video
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    // Focus trap
    modalRef.current?.focus();
  }, []);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`https://www.youtube.com/watch?v=${video.id.videoId}`);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyError(true);
      setCopied(false);
      setTimeout(() => setCopyError(false), 2000);
    }
  };

  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
    >
      <div
        className="bg-[#18181b] rounded-2xl shadow-2xl p-4 max-w-2xl w-full relative outline-none"
        onClick={e => e.stopPropagation()}
        ref={modalRef}
        tabIndex={0}
      >
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none"
          onClick={onClose}
          aria-label="Close video"
        >
          ×
        </button>
        <iframe
          width="100%"
          height="400"
          src={`https://www.youtube.com/embed/${video.id.videoId}`}
          title={video.snippet.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="rounded-xl"
        ></iframe>
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <h3 className="text-xl font-bold text-white mb-2">{video.snippet.title}</h3>
            <p className="text-gray-400 mb-2">{video.snippet.description}</p>
            <span className="text-xs text-gray-300">{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 mt-2 md:mt-0">
            <button
              className="px-3 py-1 bg-[#14F195] text-black rounded-full font-semibold text-xs hover:bg-[#0c0c15] hover:text-[#14F195] transition-colors focus:outline-none focus:ring-2 focus:ring-[#14F195] relative"
              onClick={handleShare}
              aria-label="Copy video link"
            >
              {t('videoModal.share', 'Share')}
              {copied && (
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-[#23272e] text-[#14F195] rounded text-xs shadow">{t('videoModal.copied', 'Copied!')}</span>
              )}
              {copyError && (
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-[#23272e] text-red-400 rounded text-xs shadow">{t('videoModal.failed', 'Failed!')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal; 